import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./css/menusidebar.css";
import { NavLink, useLocation } from "react-router-dom";
import { apiUrl } from "../config/api";
import { PRODUCT_WORKSPACE_MODULES } from "./commerce/productWorkspaceAccess";
import { COST_MODULES } from "./cost-control/helpers";

const MODULE_CONFIG = {
    pos: {
        label: "POS",
        path: "/app/pos",
        icon: "/images/pos.png",
    },
    inventario: {
        label: "Inventario",
        path: "/app/inventario",
        icon: "/images/inventario.png",
    },
    eventos: {
        label: "Eventos",
        path: "/app/eventos",
        icon: "/images/eventos.png",
    },
    oliva_marketing: {
        label: "Marketing y mensajes",
        path: "/app/oliva-marketing",
        materialIcon: "campaign",
        badgeKey: "websiteInquiries",
    },
    oliva_sales: {
        label: "Ventas de servicios",
        path: "/app/oliva-ventas",
        materialIcon: "point_of_sale",
    },
    mensajes_sitio: {
        label: "Conversaciones",
        path: "/app/conversaciones",
        materialIcon: "forum",
        badgeKey: "websiteInquiries",
    },
    commerce_products: {
        label: "Productos y arriendos",
        path: "/app/commerce/products",
        materialIcon: "shopping_cart",
    },
    tablee_carousel: {
        label: "Carrusel Tablee",
        path: "/app/tablee-carrusel",
        materialIcon: "view_carousel",
    },
    tablee_delivery: {
        label: "Despachos",
        path: "/app/tablee-despachos",
        materialIcon: "local_shipping",
    },
    tablee_general_config: {
        label: "Configuración General",
        path: "/app/tablee-configuracion",
        materialIcon: "settings",
    },
    tablee_payments: {
        label: "Pagos",
        path: "/app/tablee-pagos",
        materialIcon: "payments",
    },
    tablee_marketing: {
        label: "Marketing",
        path: "/app/tablee-marketing",
        materialIcon: "campaign",
    },
    tablee_promotions: {
        label: "Códigos promocionales",
        path: "/app/tablee-promociones",
        materialIcon: "sell",
    },
    tablee_home: {
        label: "Inicio Tablee",
        path: "/app/tablee-inicio",
        materialIcon: "web",
    },
    tablee_landings: {
        label: "Landings SEO",
        path: "/app/tablee-landings",
        materialIcon: "article",
    },
    tablee_ecommerce_orders: {
        label: "Ordenes",
        path: "/app/tablee-ordenes",
        materialIcon: "receipt_long",
    },
    tablee_customers: {
        label: "Usuarios Tablee",
        path: "/app/tablee-usuarios",
        materialIcon: "group",
    },
    oliva_store_marketing: { label: "Marketing de tienda", path: "/app/oliva-tienda-marketing", materialIcon: "query_stats" },
    oliva_store_campaigns: { label: "Campañas de tienda", path: "/app/oliva-tienda-campanas", materialIcon: "article" },
    oliva_ecommerce_orders: { label: "Órdenes de tienda y arriendos", path: "/app/oliva-ordenes", materialIcon: "receipt_long" },
    oliva_customers: { label: "Clientes de tienda", path: "/app/oliva-usuarios", materialIcon: "group" },
    oliva_delivery: { label: "Despachos de tienda", path: "/app/oliva-despachos", materialIcon: "local_shipping" },
    oliva_general_config: { label: "Configuración de tienda", path: "/app/oliva-configuracion", materialIcon: "settings" },
    oliva_payments: { label: "Pagos de tienda", path: "/app/oliva-pagos", materialIcon: "payments" },
    oliva_promotions: { label: "Códigos promocionales", path: "/app/oliva-promociones", materialIcon: "sell" },
    user_admin: {
        label: "Usuarios",
        path: "/app/usuarios",
        materialIcon: "manage_accounts",
    },
};

function MenuSideBar({ visible }) {
    const location = useLocation();
    const stored = localStorage.getItem("userModules");
    const sidebarRef = useRef(null);
    const flyoutRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const [flyoutGroup, setFlyoutGroup] = useState("");
    const [flyoutPosition, setFlyoutPosition] = useState({ top: 0, maxHeight: 0 });
    const [openGroups, setOpenGroups] = useState({
        kakao: true,
        oliva: true,
        tablee: true,
        commerce: true,
    });
    const [badges, setBadges] = useState({ websiteInquiries: 0 });
    const [costAdmin, setCostAdmin] = useState(false);

    useEffect(() => {
        let ignore = false;
        let session;
        try { session = JSON.parse(localStorage.getItem("sessionData") || "{}"); } catch (_) { return undefined; }
        if (!session.sessionKey || !session.username) return undefined;
        fetch(apiUrl("/user/role"), { headers: { Authorization: session.sessionKey, Username: session.username } })
            .then((response) => response.ok ? response.json() : null)
            .then((data) => { if (!ignore) setCostAdmin(["admin", "superadmin"].includes(data?.role)); })
            .catch(() => { if (!ignore) setCostAdmin(false); });
        return () => { ignore = true; };
    }, []);

    const availableModules = useMemo(() => {
        try {
            const parsed = stored ? JSON.parse(stored) : null;
            if (parsed && Array.isArray(parsed.modules)) {
                return parsed.modules.map((module) => String(module).toLowerCase());
            }
            console.warn("Expected 'modules' array not found in:", parsed);
        }
        catch (e) {
            console.error("Failed to parse 'userModules' from localStorage:", e);
        }

        return [];
    }, [stored]);

    const groups = useMemo(() => {
        const hasModule = (module) => {
            if (module === "oliva_store_marketing") return availableModules.includes("oliva_marketing");
            if (module === "oliva_store_campaigns") return availableModules.includes("oliva_general_config");
            if (module === "commerce_products") return PRODUCT_WORKSPACE_MODULES.some((key) => availableModules.includes(key));
            if (module === "oliva_payments") return availableModules.includes("oliva_payments") || availableModules.includes("oliva_general_config");
            if (module === "tablee_payments") {
                return (
                    availableModules.includes("tablee_payments") ||
                    availableModules.includes("tablee_general_config") ||
                    availableModules.includes("configuración general")
                );
            }
            return availableModules.includes(module) || availableModules.includes(MODULE_CONFIG[module]?.label?.toLowerCase());
        };

        return [
            {
                id: "commerce", label: "Comercio", icon: "storefront",
                items: ["commerce_products"].filter(hasModule).map((module) => MODULE_CONFIG[module]),
            },
            {
                id: "kakao",
                label: "KAKAO",
                logo: "/images/logowp.png",
                items: ["pos"]
                    .filter(hasModule)
                    .map((module) => MODULE_CONFIG[module]),
            },
            {
                id: "oliva",
                label: "Oliva y Miel",
                logo: "/ovm_logo.png",
                items: ["eventos", "oliva_sales", "oliva_marketing", "oliva_ecommerce_orders", "oliva_customers", "oliva_delivery", "oliva_general_config", "oliva_payments", "oliva_promotions", "oliva_store_marketing", "oliva_store_campaigns"]
                    .filter(hasModule)
                    .map((module) => MODULE_CONFIG[module]),
            },
            {
                id: "tablee",
                label: "Tablee",
                logo: "/images/tablee_logo.png",
                items: ["tablee_home", "tablee_landings", "tablee_carousel", "tablee_delivery", "tablee_general_config", "tablee_payments", "tablee_marketing", "tablee_promotions", "tablee_ecommerce_orders", "tablee_customers"]
                    .filter(hasModule)
                    .map((module) => MODULE_CONFIG[module]),
            },
        ].filter((group) => group.items.length > 0);
    }, [availableModules]);

    const hasGlobalModule = (module) => (
        availableModules.includes(module) || availableModules.includes(MODULE_CONFIG[module]?.label?.toLowerCase())
    );

    useEffect(() => {
        if (
            visible &&
            typeof window !== "undefined" &&
            window.matchMedia("(max-width: 768px)").matches
        ) {
            setExpanded(true);
            setFlyoutGroup("");
        }
    }, [visible]);

    useEffect(() => {
        const canReadOlivaContacts = availableModules.includes("oliva_contacts");
        const canReadTableeChat = availableModules.includes("mensajes_sitio")
            || availableModules.includes("mensajes del sitio");
        if (!canReadOlivaContacts && !canReadTableeChat) return;

        let ignore = false;
        const session = JSON.parse(localStorage.getItem("sessionData") || "{}");

        async function loadUnreadCount() {
            if (!session?.sessionKey || !session?.username) return;
            try {
                const headers = { Authorization: session.sessionKey, Username: session.username };
                const endpoints = [
                    ...(canReadOlivaContacts ? ["/website-inquiries/summary"] : []),
                    ...(canReadTableeChat ? ["/website-chat/summary"] : []),
                ];
                const results = await Promise.all(endpoints.map(async (endpoint) => {
                    const response = await fetch(apiUrl(endpoint), { headers });
                    const data = await response.json();
                    return data.success ? Number(data.unreadCount || 0) : 0;
                }));
                if (!ignore) {
                    setBadges((current) => ({
                        ...current,
                        websiteInquiries: results.reduce((total, value) => total + value, 0),
                    }));
                }
            } catch (err) {
                console.error("Failed to fetch website inquiry count:", err);
            }
        }

        loadUnreadCount();
        const interval = setInterval(loadUnreadCount, 60000);
        return () => {
            ignore = true;
            clearInterval(interval);
        };
    }, [availableModules]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (
                flyoutGroup &&
                sidebarRef.current &&
                !sidebarRef.current.contains(event.target)
            ) {
                setFlyoutGroup("");
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [flyoutGroup]);

    useLayoutEffect(() => {
        if (!flyoutGroup || expanded || !sidebarRef.current || !flyoutRef.current) return undefined;

        function positionFlyout() {
            const groupElement = sidebarRef.current?.querySelector(`[data-group-id="${flyoutGroup}"]`);
            const flyoutElement = flyoutRef.current;
            if (!groupElement || !flyoutElement) return;

            const viewportMargin = 12;
            const groupRect = groupElement.getBoundingClientRect();
            const buttonRect = groupElement.querySelector(".sidebar__group-button")?.getBoundingClientRect() || groupRect;
            const maxHeight = Math.max(160, window.innerHeight - (viewportMargin * 2));
            const visibleHeight = Math.min(flyoutElement.scrollHeight, maxHeight);
            const highestTop = Math.max(viewportMargin, window.innerHeight - visibleHeight - viewportMargin);
            const viewportTop = Math.min(Math.max(buttonRect.top, viewportMargin), highestTop);

            setFlyoutPosition({
                top: viewportTop - groupRect.top,
                maxHeight,
            });
        }

        positionFlyout();
        window.addEventListener("resize", positionFlyout);
        return () => window.removeEventListener("resize", positionFlyout);
    }, [expanded, flyoutGroup]);

    const toggleGroup = (groupId) => {
        if (!expanded) {
            setFlyoutGroup((current) => current === groupId ? "" : groupId);
            return;
        }

        setOpenGroups((current) => ({
            ...current,
            [groupId]: !current[groupId],
        }));
    };

    const renderItemIcon = (item) => (
        <span className="sidebar__subicon">
            {item.icon ? (
                <img src={item.icon} alt="" aria-hidden="true" />
            ) : (
                <span className="material-symbols-outlined">{item.materialIcon}</span>
            )}
            {renderBadge(item)}
        </span>
    );

    const renderBadge = (item) => {
        const value = item.badgeKey ? badges[item.badgeKey] : 0;
        if (!value) return null;
        return <span className="sidebar__badge" aria-label={`${value} mensajes nuevos`}>{value > 99 ? "99+" : value}</span>;
    };

    return (
        <aside
            className={`sidebar ${visible ? "show" : ""} ${expanded ? "sidebar--expanded" : "sidebar--compact"}`}
            ref={sidebarRef}
        >
            <nav className="sidebar__nav" aria-label="Navegación principal">
                <button
                    type="button"
                    className="sidebar__collapse"
                    onClick={() => {
                        setExpanded((value) => !value);
                        setFlyoutGroup("");
                    }}
                    aria-label={expanded ? "Contraer menú" : "Expandir menú"}
                >
                    <svg className="sidebar__tab-shape" viewBox="0 0 24 76" aria-hidden="true" focusable="false">
                        <path d="M0 0 C0 10 4 16 11 16 H15 C20 16 24 20 24 25 V51 C24 56 20 60 15 60 H11 C4 60 0 66 0 76 Z" />
                    </svg>
                    <span className="material-symbols-outlined">
                        {expanded ? "chevron_left" : "chevron_right"}
                    </span>
                </button>

                <div className="sidebar__top">
                <NavLink to="/app/home" className={({ isActive }) => `sidebar__item ${isActive ? "is-active" : ""}`}>
                    <span className="sidebar__icon material-symbols-outlined">home</span>
                    <span className="sidebar__label">Home</span>
                </NavLink>

                {(hasGlobalModule("mensajes_sitio") || hasGlobalModule("oliva_contacts")) ? (
                    <NavLink to="/app/mensajes" className={({ isActive }) => `sidebar__item ${isActive ? "is-active" : ""}`}>
                        <span className="sidebar__icon">
                            <span className="material-symbols-outlined">forum</span>
                            {renderBadge(MODULE_CONFIG.mensajes_sitio)}
                        </span>
                        <span className="sidebar__label">Mensajes</span>
                    </NavLink>
                ) : null}

                {hasGlobalModule("user_admin") ? (
                    <NavLink to="/app/usuarios" className={({ isActive }) => `sidebar__item ${isActive ? "is-active" : ""}`}>
                        <span className="sidebar__icon material-symbols-outlined">manage_accounts</span>
                        <span className="sidebar__label">Usuarios</span>
                    </NavLink>
                ) : null}

                </div>
                <hr className="sidebar__divider" />
                <div className="sidebar__lower" role="region" aria-label="Herramientas y negocios" tabIndex={expanded ? 0 : undefined}>
                {(costAdmin || COST_MODULES.some((module) => availableModules.includes(module))) ? (
                    <NavLink to="/app/control-costos" className={({ isActive }) => `sidebar__item ${isActive ? "is-active" : ""}`}>
                        <span className="sidebar__icon material-symbols-outlined">account_balance_wallet</span>
                        <span className="sidebar__label">Control de costos</span>
                    </NavLink>
                ) : null}

                <div className="sidebar__groups">
                    {groups.map((group) => {
                        const groupActive = group.items.some((item) => location.pathname === item.path);
                        const groupOpen = openGroups[group.id];

                        return (
                            <section
                                className={`sidebar__group ${groupActive ? "is-active" : ""} ${flyoutGroup === group.id ? "has-flyout" : ""}`}
                                data-group-id={group.id}
                                key={group.id}
                            >
                                <button
                                    type="button"
                                    className="sidebar__group-button"
                                    onClick={() => toggleGroup(group.id)}
                                    aria-expanded={expanded ? groupOpen : flyoutGroup === group.id}
                                >
                                    <span className={`sidebar__icon ${group.logo ? `sidebar__icon--${group.id}` : ""}`}>
                                        {group.logo ? (
                                            <img src={group.logo} alt="" aria-hidden="true" />
                                        ) : (
                                            <span className="material-symbols-outlined">{group.icon}</span>
                                        )}
                                    </span>
                                    <span className="sidebar__label">{group.label}</span>
                                    <span className="sidebar__chevron material-symbols-outlined">
                                        {groupOpen ? "expand_less" : "expand_more"}
                                    </span>
                                </button>

                                {expanded && groupOpen ? (
                                    <div className="sidebar__submenu">
                                        {group.items.map((item) => (
                                            <NavLink
                                                key={item.path}
                                                to={item.path}
                                                className={({ isActive }) => `sidebar__subitem ${isActive ? "is-active" : ""}`}
                                            >
                                                {renderItemIcon(item)}
                                                <span className="sidebar__label">{item.label}</span>
                                            </NavLink>
                                        ))}
                                    </div>
                                ) : null}

                                {!expanded && flyoutGroup === group.id ? (
                                    <div
                                        className="sidebar__flyout"
                                        ref={flyoutRef}
                                        style={{
                                            top: `${flyoutPosition.top}px`,
                                            maxHeight: `${flyoutPosition.maxHeight}px`,
                                        }}
                                    >
                                        <div className="sidebar__flyout-title">{group.label}</div>
                                        {group.items.map((item) => (
                                            <NavLink
                                                key={item.path}
                                                to={item.path}
                                                className={({ isActive }) => `sidebar__flyout-item ${isActive ? "is-active" : ""}`}
                                                onClick={() => setFlyoutGroup("")}
                                            >
                                                {renderItemIcon(item)}
                                                <span>{item.label}</span>
                                            </NavLink>
                                        ))}
                                    </div>
                                ) : null}
                            </section>
                        );
                    })}
                </div>
                </div>
            </nav>
        </aside>
    );
}

export default MenuSideBar;
